/// <reference path="../DataTypes/types.js" />
import SettingsService from './SettingsService';
import DateService from './DateService';

// Constants
const DEFAULT_TITAN_EMAIL = 'addressnotprovided@titancontainer.dk';
const VERIFICATION_POLLING_INTERVAL_MS = 5000; // 5 seconds
const VERIFICATION_POLLING_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const API_BASE_URL =
  process.env.REACT_APP_CONSUMER_API_URL + '/api/CheckoutApi';

class BookingService {
  constructor() {
    this.paymentService = null;
  }

  static formatDateFrom(dateFrom) {
    return DateService.toUtcMidnightIso(dateFrom);
  }

  setPaymentService(paymentService) {
    this.paymentService = paymentService;
  }

  /**
   * Create the Stripe customer used to run the verification and later billing
   */
  async createStripeCustomer(user) {
    if (!this.paymentService) {
      throw new Error('PaymentService not set');
    }

    const customer = await this.paymentService.createStripeCustomer(user);

    if (!customer?.stripeUserId) {
      throw new Error('error_creating_stripe_customer');
    }

    return customer;
  }

  /**
   * Build debtor account payload from user data.
   * userId is the real Stripe customer id created before verification.
   */
  buildDebtorAccountPayload(user) {
    return {
      userId: user.stripeCustomerId ?? '',
      userEmail: user.email,
      userName: user.firstName + ' ' + user.lastName,
      phone: user.phone,
      country: user.address.country,
      countryShortName: SettingsService.getCountryShortCode(),
      cvr: user.cvr,
      city: user.address.city,
      line1: user.address.streetAddress,
      state: user.address.state,
      postalCode: user.address.zipCode,
    };
  }

  /**
   * Build booking order payload from booking data
   */
  buildBookingOrderPayload(debtor, user, room, currentLocation, dateFrom) {
    return {
      debtorAccountId: debtor.debtorAccountId,
      countryShortName: SettingsService.getCountryShortCode(),
      roomId: room.Id,
      dateFrom: DateService.formatDate(dateFrom),
      userEmail: user.email,
      phone: user.phone,
      titanEmail: currentLocation.email || DEFAULT_TITAN_EMAIL,
      containerNumber: room.Number,
      containerPrefix: room.Prefix,
      locationName: currentLocation.name,
    };
  }

  /**
   * Create or get existing debtor account
   */
  async createDebtorAccount(user, onStatusUpdate) {
    if (!this.paymentService) {
      throw new Error('PaymentService not set');
    }

    onStatusUpdate?.('loading_text_booking_creation');
    const debtorPayload = this.buildDebtorAccountPayload(user);

    const debtor =
      await this.paymentService.GetOrCreateDebtorAccount(debtorPayload);

    if (!debtor) {
      onStatusUpdate?.('error_creating_debtor_account');
      throw new Error('Debtor account creation failed');
    }

    return debtor;
  }

  /**
   * Create a booking order
   */
  async createOrder(debtor, user, room, currentLocation, dateFrom) {
    if (!this.paymentService) {
      throw new Error('PaymentService not set');
    }

    const orderPayload = this.buildBookingOrderPayload(
      debtor,
      user,
      room,
      currentLocation,
      dateFrom
    );

    const order = await this.paymentService.CreateBookingOrder(orderPayload);
    return order;
  }

  /**
   * Start Stripe identity verification
   */
  async startVerification(stripePromise, sessionClientSecret) {
    const stripe = await stripePromise;
    const { error } = await stripe.verifyIdentity(sessionClientSecret);

    if (error) {
      return { error };
    }

    return { error: null };
  }

  /**
   * Poll verification session status until it is no longer processing or aborted.
   * Stops after VERIFICATION_POLLING_TIMEOUT_MS while Stripe reports the session as processing.
   * Returns: { verified } | { cancelled } | { requiredInput } | { denied } | { timedOut }
   */
  async pollVerificationStatus(session, signal) {
    if (!this.paymentService) {
      throw new Error('PaymentService not set');
    }

    return new Promise((resolve, reject) => {
      let intervalId = null;

      //Extra check for polling timeout should never be used hopefully.
      const startTime = Date.now();

      const cleanup = () => {
        if (intervalId !== null) {
          clearInterval(intervalId);
          intervalId = null;
        }
        signal?.removeEventListener('abort', onAbort);
      };

      const onAbort = () => {
        cleanup();
        resolve({ verified: false, cancelled: true });
      };

      if (signal?.aborted) {
        resolve({ verified: false, cancelled: true });
        return;
      }

      signal?.addEventListener('abort', onAbort);

      intervalId = setInterval(async () => {
        try {
          if (Date.now() - startTime >= VERIFICATION_POLLING_TIMEOUT_MS) {
            cleanup();
            resolve({ verified: false, timedOut: true });
            return;
          }

          const responseData =
            await this.paymentService.checkVerificationSessionStatus(
              session.verificationSessionId,
              session.userEmail
            );

          if (responseData.isProcessing === true) {
            return;
          }

          cleanup();

          if (responseData.verificationStatus === true) {
            resolve({ verified: true });
          } else if (responseData.requiredUserInput) {
            resolve({ verified: false, requiredInput: true });
          } else {
            resolve({ verified: false, denied: true });
          }
        } catch (error) {
          cleanup();
          reject(error);
        }
      }, VERIFICATION_POLLING_INTERVAL_MS);
    });
  }

  /**
   * Handle the verification flow (create session, start verification, poll status)
   */
  async handleVerificationFlow(
    user,
    room,
    dateFrom,
    titanEmail,
    stripeUserId,
    stripePromise,
    currentLocation,
    onStatusUpdate,
    signal
  ) {
    const session = await this.paymentService.createVerificationSession(
      user.email,
      titanEmail,
      room.Id,
      dateFrom,
      currentLocation.name,
      stripeUserId
    );

    if (!session.clientSecret) {
      throw new Error('No client secret received from verification session');
    }

    // Step 2: Start verification
    const verifyResult = await this.startVerification(
      stripePromise,
      session.clientSecret
    );

    if (verifyResult.error) {
      return { success: false, closed: true };
    }

    // Step 3: Poll verification status until resolved or aborted
    onStatusUpdate('loading_text_verification_submitted');
    const pollResult = await this.pollVerificationStatus(
      { ...session, userEmail: user.email },
      signal
    );

    if (pollResult.verified) {
      return { success: true };
    } else if (pollResult.cancelled) {
      return { success: false, cancelled: true };
    } else if (pollResult.requiredInput) {
      return { success: false, requiredInput: true };
    } else if (pollResult.timedOut) {
      return { success: false, timedOut: true };
    } else {
      return { success: false, denied: true };
    }
  }

  /**
   * Create the debtor account and booking order after a successful verification
   */
  async finalizeBooking(user, room, currentLocation, dateFrom, onStatusUpdate) {
    onStatusUpdate?.('loading_text_booking_creation');

    const debtor = await this.createDebtorAccount(user, onStatusUpdate);

    const order = await this.createOrder(
      debtor,
      user,
      room,
      currentLocation,
      dateFrom
    );

    return { debtor, order };
  }
}

const bookingService = new BookingService();

export default bookingService;
